/**
 * Hook for fetching and managing Encompass eFolder documents
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getEncompassDocuments,
  getLoanDocumentsSummary,
  triggerDocumentSync
} from '@/services/encompassDocumentService';
import type {
  EncompassDocumentWithAttachments,
  LoanDocumentsSummary
} from '@/types/encompass-documents';

interface UseEncompassDocumentsResult {
  documents: EncompassDocumentWithAttachments[];
  summary: LoanDocumentsSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  syncDocuments: () => Promise<{ success: boolean; message: string }>;
  syncing: boolean;
}

export function useEncompassDocuments(loanId: string): UseEncompassDocumentsResult {
  const [documents, setDocuments] = useState<EncompassDocumentWithAttachments[]>([]);
  const [summary, setSummary] = useState<LoanDocumentsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const fetchData = useCallback(async () => {
    if (!loanId) {
      setDocuments([]);
      setSummary(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [docs, summaryData] = await Promise.all([
        getEncompassDocuments(loanId),
        getLoanDocumentsSummary(loanId)
      ]);

      setDocuments(docs);
      setSummary(summaryData);
    } catch (err) {
      console.error('Failed to fetch Encompass documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [loanId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const syncDocuments = useCallback(async () => {
    if (!loanId) {
      return { success: false, message: 'No loan ID provided' };
    }

    setSyncing(true);

    try {
      const result = await triggerDocumentSync(loanId);

      if (result.success) {
        // Refetch after successful sync
        await fetchData();
      }

      return result;
    } finally {
      setSyncing(false);
    }
  }, [loanId, fetchData]);

  return {
    documents,
    summary,
    loading,
    error,
    refetch: fetchData,
    syncDocuments,
    syncing
  };
}
